import { get, useIntervalFn } from '@vueuse/core';
import type { MaybeRef } from 'vue';
import { computed, ref, watch } from 'vue';
import type { IBetBuilderRecommendation } from '@/interface/IBetBuilderRecommendation';
import { RecommendationApi } from '@/libraries/recommendation/recommendationApi';
import type { BetBuilderRecommendationsParams } from '@/libraries/recommendation/recommendationApi';

const liveRecommendationsPollIntervalMs = 5_000;
const nonLiveRecommendationsPollIntervalMs = 15_000;

export function useBetBuilderRecommendations(
    params: BetBuilderRecommendationsParams = {},
    isEnabled: MaybeRef<boolean> = true,
) {
    const betBuilderRecommendations = ref<IBetBuilderRecommendation[]>([]);
    const isLoaded = ref(false);

    const pollIntervalMs = computed(() => (params.isLivePage === true
        ? liveRecommendationsPollIntervalMs
        : nonLiveRecommendationsPollIntervalMs));

    async function fetchBetBuilderRecommendations() {
        if (!get(isEnabled)) {
            betBuilderRecommendations.value = [];
            isLoaded.value = true;
            return;
        }

        try {
            betBuilderRecommendations.value = (await RecommendationApi.getBetBuilderRecommendations(params));
        } catch (error) {
            console.error('Failed to fetch bet builder recommendations', error);
        } finally {
            isLoaded.value = true;
        }
    }

    watch(
        () => [get(isEnabled), params.isLivePage],
        () => {
            fetchBetBuilderRecommendations();
        },
    );

    useIntervalFn(() => {
        if (get(isEnabled)) {
            fetchBetBuilderRecommendations();
        }
    }, pollIntervalMs);

    fetchBetBuilderRecommendations();

    return {
        betBuilderRecommendations,
        isLoaded,
    };
}
